import { ATTACHMENT_ACCEPTED_MIME, ATTACHMENT_MAX_BYTES } from '@/types/cgt'

/**
 * Pure rules for the equipment-documents UI (ClickUp z8nrz7cnj9).
 *
 * Separate from the component because `vitest.config.ts` is `environment: node`
 * with `include: ['src/**\/*.test.ts']` — a `.tsx` test would silently never
 * run. Everything here is therefore plain logic with real tests, and the panel
 * only renders what these return.
 */

/**
 * Why a picked file was refused, or that it wasn't.
 *
 * A structured result rather than a formatted string: the size message wants
 * `formatBytes`, which lives with the renderer's attachment components, and a
 * pure module has no business importing a React module to build a sentence.
 */
export type DocumentFileCheck =
  | { ok: true }
  | { ok: false; reason: 'type' }
  | { ok: false; reason: 'size'; sizeBytes: number; limitBytes: number }

/**
 * Mirrors the server's presign guard. Client-side it is a courtesy — the server
 * refuses the same file — but it turns a round trip and a raw 400 into an
 * instant, specific message.
 */
export function checkDocumentFile(file: { type: string; size: number }): DocumentFileCheck {
  if (!ATTACHMENT_ACCEPTED_MIME.includes(file.type)) return { ok: false, reason: 'type' }
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return { ok: false, reason: 'size', sizeBytes: file.size, limitBytes: ATTACHMENT_MAX_BYTES }
  }
  return { ok: true }
}

/**
 * Whether the Add / Save control may fire.
 *
 * A name is required — that is the whole point of the feature, since the list
 * reads by name rather than by file name. Whitespace does not count as one; the
 * server trims too, so `"   "` would otherwise be stored as an empty label.
 */
export function isValidDocumentName(name: string): boolean {
  return name.trim().length > 0
}

/**
 * Whether a document can be shown inline, or only offered as a download.
 *
 * PDFs are deliberately NOT previewable despite being the common case here. A
 * presigned URL in an `<iframe>` is blocked by the CSP on some deployments, and
 * an empty frame reads as a broken feature — the renderer's AttachmentPreview
 * reached the same conclusion and says so in its own comment.
 *
 * The renderer duplicates this predicate twice already (AttachmentPreview and
 * AttachmentThumb each declare their own). This copy is the only one vitest can
 * reach, since both of those live in `.tsx` files the config cannot run.
 */
export function isPreviewableImage(mimeType: string): boolean {
  return mimeType === 'image/png' || mimeType === 'image/jpeg'
}

/**
 * Default name offered when a file is picked: the file name without its
 * extension, tidied up. Saves typing in the common case and is fully
 * overwritable — the user's own text always wins.
 */
export function suggestDocumentName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^./\\]+$/, '')
  return withoutExtension.replace(/[_-]+/g, ' ').trim() || fileName
}
